import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import api from "../api";
import { Link } from "react-router-dom";
import Shell from "../Shell";
import { authHeaders } from "../api";

/* ───────────────────────────── Date helpers ───────────────────────────── */

const isoToDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dateToIso = (date) => {
  if (!(date instanceof Date)) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fmtDMY = (iso) => {
  const dt = isoToDate(iso);
  if (!dt) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (String(dt) === "Invalid Date") return d;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/* ─────────────── Tiny inline calendar (DD/MM view, ISO value) ─────────────── */

function MiniDatePickerDMY({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = isoToDate(value) || new Date();
  const [view, setView] = useState(new Date(selected.getFullYear(), selected.getMonth(), 1));
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const s = isoToDate(value);
    if (s) setView(new Date(s.getFullYear(), s.getMonth(), 1));
  }, [value]);

  const startOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const startDay = (startOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();

  const prevMonth = () => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const nextMonth = () => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.getFullYear(), view.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);

  const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <input
        readOnly
        value={fmtDMY(value)}
        onClick={() => setOpen((v) => !v)}
        style={{ width: 160, cursor: "pointer" }}
        placeholder="dd/mm/áááá"
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: 38,
            left: 0,
            background: "#fff",
            border: "1px solid #d7dbe3",
            borderRadius: 8,
            padding: 8,
            zIndex: 50,
            boxShadow: "0 12px 28px rgba(0,0,0,.12)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
            <button className="btn ghost sm" type="button" onClick={prevMonth}>◀</button>
            <div style={{ fontWeight: 600 }}>
              {view.toLocaleString("is-IS", { month: "long" })} {view.getFullYear()}
            </div>
            <button className="btn ghost sm" type="button" onClick={nextMonth}>▶</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 4, textAlign: "center", opacity: 0.7, fontSize: 12, marginBottom: 4 }}>
            {["Mán", "Þri", "Mið", "Fim", "Fös", "Lau", "Sun"].map((d) => <div key={d}>{d}</div>)}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 32px)", gap: 4, textAlign: "center" }}>
            {cells.map((c, i) => {
              const isOut = c === null || c.getMonth() !== view.getMonth();
              const active = c && isSameDay(c, selected);
              return (
                <button
                  key={i}
                  type="button"
                  disabled={!c}
                  onClick={() => {
                    if (!c) return;
                    onChange?.(dateToIso(c));
                    setOpen(false);
                  }}
                  style={{
                    width: 32,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid #d7dbe3",
                    opacity: isOut ? 0.35 : 1,
                    background: active ? "#e0ecff" : "#fff",
                    cursor: c ? "pointer" : "default",
                  }}
                >
                  {c ? c.getDate() : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────── Debounce ───────────────────────────── */

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/* ───────────────────────────── Excel export config ───────────────────────────── */

const EXPORT_FIELDS = [
  { key: "full_name", label: "Nafn" },
  { key: "username", label: "Notendanafn" },
  { key: "place", label: "Sveitarfélag" },
  { key: "phone_number", label: "Símanúmer" },
  { key: "email", label: "Netfang" },
  { key: "ssn", label: "Kennitala" },
  { key: "is_in_rehab", label: "Er í endurhæfingu" },
  { key: "current_rehab", label: "Skipulag endurhæfingar" },
  { key: "is_in_urraedi", label: "Er í úrræði" },
  { key: "is_teamsari", label: "Teamsari" },
  { key: "created_at", label: "Stofnaður" },
  { key: "started_at_hugarafl", label: "Byrjaði í Hugarafli" },
  { key: "attended_last", label: "Mætti síðast" },
];

const DEFAULT_EXPORT_FIELD_KEYS = [
  "full_name",
];

function ExportModal({
  open,
  selectedKeys,
  onToggle,
  onMoveUp,
  onMoveDown,
  onClose,
  onDownload,
}) {
  if (!open) return null;

  const selectedFields = selectedKeys
    .map((key) => EXPORT_FIELDS.find((f) => f.key === key))
    .filter(Boolean);

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ width: "min(760px, 94vw)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Sækja Excel</div>
            <div style={{ opacity: 0.7, marginTop: 4 }}>
              Veldu hvaða dálkar eiga að fara í skjalið og í hvaða röð.
            </div>
          </div>
          <button className="btn ghost sm" onClick={onClose}>Loka</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Veljanleg svið</div>
            <div style={{ display: "grid", gap: 8 }}>
              {EXPORT_FIELDS.map((field) => {
                const checked = selectedKeys.includes(field.key);
                return (
                  <label
                    key={field.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      border: "1px solid #d7dbe3",
                      borderRadius: 10,
                      padding: "8px 10px",
                      background: checked ? "#f8fbff" : "#fff",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(field.key)}
                    />
                    <span>{field.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Röð í Excel</div>
            <div style={{ display: "grid", gap: 8 }}>
              {selectedFields.length === 0 ? (
                <div style={{ opacity: 0.7 }}>Ekkert svið valið.</div>
              ) : (
                selectedFields.map((field, index) => (
                  <div
                    key={field.key}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: 8,
                      alignItems: "center",
                      border: "1px solid #d7dbe3",
                      borderRadius: 10,
                      padding: "8px 10px",
                      background: "#fff",
                    }}
                  >
                    <div>
                      {index + 1}. {field.label}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        className="btn ghost sm"
                        onClick={() => onMoveUp(field.key)}
                        disabled={index === 0}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn ghost sm"
                        onClick={() => onMoveDown(field.key)}
                        disabled={index === selectedFields.length - 1}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button className="btn ghost sm" onClick={onClose}>Hætta við</button>
          <button className="btn sm" onClick={onDownload}>Sækja .xlsx</button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────── Component ───────────────────────────── */

export default function MembersList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [searchIn, setSearchIn] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [attendedStart, setAttendedStart] = useState("");
  const [attendedEnd, setAttendedEnd] = useState("");

  const [createdStart, setCreatedStart] = useState("");
  const [createdEnd, setCreatedEnd] = useState("");

  const [startedStart, setStartedStart] = useState("");
  const [startedEnd, setStartedEnd] = useState("");

  const [orderBy, setOrderBy] = useState("name");
  const [roleFilter, setRoleFilter] = useState("");
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const [copyMsg, setCopyMsg] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [selectedExportKeys, setSelectedExportKeys] = useState(DEFAULT_EXPORT_FIELD_KEYS);

  const dq = useDebounced(q, 350);

  const params = useMemo(() => {
    const p = {};
    if (dq.trim()) p.q = dq.trim();
    if (searchIn) p.search_in = searchIn;

    if (attendedStart) p.attended_start = attendedStart;
    if (attendedEnd) p.attended_end = attendedEnd;

    if (createdStart) p.created_start = createdStart;
    if (createdEnd) p.created_end = createdEnd;

    if (startedStart) p.started_start = startedStart;
    if (startedEnd) p.started_end = startedEnd;

    if (orderBy) p.order_by = orderBy;

    if (roleFilter) {
      if (roleFilter === "rehab") {
        p.in_rehab = 1;
      } else if (roleFilter === "urraedi") {
        p.in_urraedi = 1;
      } else if (roleFilter === "teamsari") {
        p.in_teamsari = 1;
      } else {
        p.role = roleFilter;
      }
    }

    return p;
  }, [
    dq,
    searchIn,
    attendedStart,
    attendedEnd,
    createdStart,
    createdEnd,
    startedStart,
    startedEnd,
    orderBy,
    roleFilter,
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/users-list/", {
        params,
        headers: authHeaders(),
      });
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setOffset(0);
  }, [
    dq,
    searchIn,
    attendedStart,
    attendedEnd,
    createdStart,
    createdEnd,
    startedStart,
    startedEnd,
    orderBy,
    roleFilter,
    limit,
  ]);

  const total = rows.length;
  const page = rows.slice(offset, offset + limit);

  const onPrev = () => setOffset((o) => Math.max(0, o - limit));
  const onNext = () => setOffset((o) => (o + limit < total ? o + limit : o));

  async function copyEmails() {
    const emails = Array.from(
      new Set(
        rows
          .map((u) => String(u.email || "").trim())
          .filter((e) => e && e.includes("@"))
      )
    );
    const payload = emails.join("; ");
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(payload);
      } else {
        const ta = document.createElement("textarea");
        ta.value = payload;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopyMsg(`Afritaði ${emails.length} netföng.`);
      setTimeout(() => setCopyMsg(""), 2500);
    } catch {
      setCopyMsg("Tókst ekki að afrita.");
      setTimeout(() => setCopyMsg(""), 2500);
    }
  }

  function toggleExportField(key) {
    setSelectedExportKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  }

  function moveExportField(key, direction) {
    setSelectedExportKeys((prev) => {
      const index = prev.indexOf(key);
      if (index === -1) return prev;

      const next = [...prev];
      const swapIndex = direction === "up" ? index - 1 : index + 1;

      if (swapIndex < 0 || swapIndex >= next.length) return prev;

      [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
      return next;
    });
  }

  function exportValue(user, key) {
    switch (key) {
      case "full_name":
        return user.full_name || "";
      case "username":
        return user.username || "";
      case "place":
        return user.place || "";
      case "phone_number":
        return user.phone_number || "";
      case "email":
        return user.email || "";
      case "ssn":
        return user.ssn || "";
      case "is_in_rehab":
        return user.is_in_rehab ? "Já" : "Nei";
      case "current_rehab":
        return user.current_rehab || "";
      case "is_in_urraedi":
        return user.is_in_urraedi ? "Já" : "Nei";
      case "is_teamsari":
        return user.is_teamsari ? "Já" : "Nei";
      case "created_at":
        return fmtDate(user.created_at);
      case "started_at_hugarafl":
        return fmtDate(user.started_at_hugarafl);
      case "attended_last":
        return fmtDate(user.attended_last);
      default:
        return "";
    }
  }

  function downloadExcel() {
    if (!selectedExportKeys.length) {
      alert("Veldu að minnsta kosti eitt svið.");
      return;
    }

    const selectedFields = selectedExportKeys
      .map((key) => EXPORT_FIELDS.find((f) => f.key === key))
      .filter(Boolean);

    const headerRow = selectedFields.map((f) => f.label);
    const bodyRows = rows.map((user) => selectedFields.map((f) => exportValue(user, f.key)));

    const ws = XLSX.utils.aoa_to_sheet([headerRow, ...bodyRows]);

    ws["!cols"] = selectedFields.map((field, colIndex) => {
      const lengths = [field.label.length, ...bodyRows.map((row) => String(row[colIndex] ?? "").length)];
      const maxLen = Math.max(...lengths, 10);
      return { wch: Math.min(maxLen + 2, 40) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Félagsaðilar");

    const today = dateToIso(new Date()) || "export";
    XLSX.writeFile(wb, `felagsadilar_${today}.xlsx`);

    setExportOpen(false);
  }

  return (
    <Shell
      title="Hugarafl starfsfólk"
      rightMenu
      menuItems={[
        { title: "Samþykkja nýjan félagsaðila", to: "/members/approve" },
        { title: "Yfirlit yfir félagsaðila", to: "/members" },
        { title: "Yfirlit yfir hópa", to: "/groups" },
        { title: "Skrá viðtöl", to: "/interviews/new" },
        { title: "Skrá atvik", to: "/incidents/new" },
        { title: "Skrá mætingu í hóp", to: "/attendance/group" },
        { title: "Heildaryfirlit yfir mætingu", to: "/attendance/summary" },
      ]}
    >
      <h2 style={{ textAlign: "center", margin: "8px 0 18px" }}>
        Yfirlit yfir félagsaðila
      </h2>

      <div className="members-toolbar" style={{ justifyContent: "space-between" }}>
        <div className="search-block" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label className="sr" htmlFor="search">Leita</label>
          <input
            id="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Leitaðu hér — eða notaðu "Falin"'
            style={{ width: 280 }}
          />

          <select value={searchIn} onChange={(e) => setSearchIn(e.target.value)}>
            <option value="all">Leita í öllum dálkum</option>
            <option value="full_name">Aðeins nafn</option>
            <option value="username">Aðeins notendanafn</option>
            <option value="email">Aðeins netfang</option>
            <option value="place">Aðeins sveitarfélag</option>
            <option value="current_rehab">Aðeins skipulag endurhæfingar</option>
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{
            border: "2px solid #222",
            borderRadius: 6,
            padding: "6px 12px",
            textAlign: "center",
            minWidth: 180,
            fontWeight: 600
          }}>
            Félagsaðilar samtals
            <div style={{ fontSize: 18 }}>{total}</div>
          </div>

          <button className="btn sm" onClick={copyEmails} title="Afrita öll síuð netföng (Outlook snið)">
            Afrita netföng
          </button>

          <button className="btn sm ghost" onClick={() => setExportOpen(true)}>
            Sækja Excel
          </button>

          {copyMsg ? <span style={{ opacity: 0.8 }}>{copyMsg}</span> : null}
        </div>

        <div className="filter-block">
          <button className="btn sm" onClick={() => setFiltersOpen(v => !v)}>
            Sía/röð
          </button>

          {filtersOpen && (
            <div className="filter-popover" style={{ minWidth: 340 }}>
              <div className="filter-title">Mætti síðast:</div>
              <div className="filter-row">
                <label>Byrjunardagsetning:</label>
                <MiniDatePickerDMY value={attendedStart} onChange={setAttendedStart} />
              </div>
              <div className="filter-row">
                <label>Endadagsetning:</label>
                <MiniDatePickerDMY value={attendedEnd} onChange={setAttendedEnd} />
              </div>

              <div className="filter-title" style={{ marginTop: 10 }}>Stofnaður:</div>
              <div className="filter-row">
                <label>Byrjunardagsetning:</label>
                <MiniDatePickerDMY value={createdStart} onChange={setCreatedStart} />
              </div>
              <div className="filter-row">
                <label>Endadagsetning:</label>
                <MiniDatePickerDMY value={createdEnd} onChange={setCreatedEnd} />
              </div>

              <div className="filter-title" style={{ marginTop: 10 }}>Byrjaði í Hugarafli:</div>
              <div className="filter-row">
                <label>Byrjunardagsetning:</label>
                <MiniDatePickerDMY value={startedStart} onChange={setStartedStart} />
              </div>
              <div className="filter-row">
                <label>Endadagsetning:</label>
                <MiniDatePickerDMY value={startedEnd} onChange={setStartedEnd} />
              </div>

              <div className="filter-title" style={{ marginTop: 8 }}>Sía eftir flokki:</div>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="">Allir</option>
                <option value="rehab">Í endurhæfingu</option>
                <option value="urraedi">Í úrræði</option>
                <option value="teamsari">Teamsarar</option>
                <option value="peer_support">Jafningjastuðningsaðilar</option>
                <option value="volunteer">Sjálfboðaliðar</option>
                <option value="leader">Hópstjórar</option>
                <option value="staff">Starfsfólk</option>
                <option value="none">Ekkert af ofangreindu</option>
              </select>

              <div className="filter-title" style={{ marginTop: 8 }}>Raða eftir:</div>
              <select value={orderBy} onChange={(e) => setOrderBy(e.target.value)}>
                <option value="name">Nafn</option>
                <option value="email">Netfang</option>
                <option value="created_at">Stofnaður</option>
                <option value="started_at_hugarafl">Byrjaði í Hugarafli</option>
                <option value="last_attended">Mætti síðast</option>
              </select>

              <div className="filter-title" style={{ marginTop: 8 }}>Fjöldi á síðu:</div>
              <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}>
                {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
              </select>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
                <button
                  className="btn ghost sm"
                  onClick={() => {
                    setQ("");
                    setSearchIn("all");
                    setAttendedStart("");
                    setAttendedEnd("");
                    setCreatedStart("");
                    setCreatedEnd("");
                    setStartedStart("");
                    setStartedEnd("");
                    setOrderBy("name");
                    setRoleFilter("");
                    setLimit(100);
                  }}
                >
                  Hreinsa síur
                </button>
                <button className="btn ghost sm" onClick={() => setFiltersOpen(false)}>
                  Loka síu
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Hleð…</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table zebra">
              <thead>
                <tr>
                  <th>Nafn</th>
                  <th>Notendanafn</th>
                  <th>Sveitarfélag</th>
                  <th>Netfang</th>
                  <th>Er í endurhæfingu</th>
                  <th>Skipulag endurhæfingar</th>
                  <th>Er í úrræði</th>
                  <th>Stofnaður</th>
                  <th>Byrjaði í Hugarafli</th>
                  <th>Mætti síðast</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {page.length === 0 ? (
                  <tr>
                    <td colSpan={11} style={{ textAlign: "center", padding: 16 }}>
                      Engir félagsaðilar fundust
                    </td>
                  </tr>
                ) : (
                  page.map((u) => (
                    <tr key={u.id}>
                      <td>{u.full_name}</td>
                      <td>{u.username}</td>
                      <td>{u.place || "—"}</td>
                      <td>{u.email}</td>
                      <td>{u.is_in_rehab ? "Já" : "Nei"}</td>
                      <td>{u.current_rehab || "—"}</td>
                      <td>{u.is_in_urraedi ? "Já" : "Nei"}</td>
                      <td>{fmtDate(u.created_at)}</td>
                      <td>{fmtDate(u.started_at_hugarafl)}</td>
                      <td>{fmtDate(u.attended_last)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <Link className="btn sm ghost" to={`/members/${u.id}`}>Nánar</Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
            <div style={{ opacity: 0.8 }}>
              Sýni {page.length} af {total}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn sm ghost" onClick={onPrev} disabled={offset === 0}>Fyrri</button>
              <button className="btn sm" onClick={onNext} disabled={offset + limit >= total}>Næsta</button>
            </div>
          </div>
        </>
      )}

      <ExportModal
        open={exportOpen}
        selectedKeys={selectedExportKeys}
        onToggle={toggleExportField}
        onMoveUp={(key) => moveExportField(key, "up")}
        onMoveDown={(key) => moveExportField(key, "down")}
        onClose={() => setExportOpen(false)}
        onDownload={downloadExcel}
      />
    </Shell>
  );
}